import React from 'react';
import styled from 'styled-components';
import Sidebar from './Sidebar';
import { SidebarProvider, useSidebarContext } from '../contexts/SidebarContext';
import HelpAssistantWidget from '../features/help-assistant/HelpAssistantWidget';

const MOBILE_BREAKPOINT = 768;

const MobileOverlay = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

const PersistentSidebar: React.FC = () => {
  const {
    isMobile,
    shouldShowSidebar,
    sidebarWidth,
    isMinimized,
    toggleCollapsed,
    setCollapsed,
  } = useSidebarContext();

  const closeSidebar = () => setCollapsed(true);

  return (
    <>
      <Sidebar
        width={sidebarWidth}
        isMobile={isMobile}
        isVisible={shouldShowSidebar}
        isMinimized={isMinimized}
        onClose={closeSidebar}
        onToggleCollapse={toggleCollapsed}
      />
      <MobileOverlay $show={isMobile && shouldShowSidebar} onClick={closeSidebar} />
    </>
  );
};

/**
 * App frame that survives navigation. The sidebar is rendered here — outside
 * the <Routes>/<Suspense> subtree — so switching pages (and waiting for a
 * lazily loaded page chunk) no longer tears it down and rebuilds it.
 * Sidebar renders nothing when nobody is logged in, so public routes are
 * unaffected.
 *
 * The help assistant hangs here for the same reason: its open state and the
 * whole conversation must survive navigation, and it must be reachable from
 * every authenticated page (it renders nothing on the public ones).
 */
const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SidebarProvider>
    <PersistentSidebar />
    {children}
    <HelpAssistantWidget />
  </SidebarProvider>
);

export default AppShell;
